import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MultiSelect } from '../ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CheckToggle } from './CheckToggle';
import { NotificationCardHeader } from './NotificationCardHeader';
import { NotificationCardError } from './NotificationCardError';
import { NotificationHourSelect } from './NotificationHourSelect';
import { NotificationMessageEditor } from './NotificationMessageEditor';
import { NotificationResetFooter } from './NotificationResetFooter';
import { NotifyRoleToggles } from './NotifyRoleToggles';
import { ReviewReminderNotifyRole, ReviewReminderSendMode } from '../../services/slackService';

export const MAX_LEAD_TIME_DAYS = 365;
export const MAX_LEAD_TIME_ENTRIES = 5;

/** Selectable recipient roles, ordered from broadest to narrowest scope. */
export const NOTIFY_ROLE_OPTIONS: ReviewReminderNotifyRole[] = [
  ReviewReminderNotifyRole.ADMIN,
  ReviewReminderNotifyRole.MANAGER,
  ReviewReminderNotifyRole.MEMBER,
];

export interface ReviewReminderSettingsDraft {
  channel: string;
  sendToChannel: boolean;
  notificationHour: number;
  daysBefore: number[];
  notifyRoles: ReviewReminderNotifyRole[];
  sendMode: ReviewReminderSendMode;
}

/** True when the draft exceeds the lead-time count the backend accepts. */
export const hasTooManyLeadTimes = (draft: ReviewReminderSettingsDraft): boolean =>
  draft.daysBefore.length > MAX_LEAD_TIME_ENTRIES;

/**
 * True when the reminder would reach nobody: no recipient role selected and no
 * channel post (the backend also drops the channel post when the channel is
 * blank), i.e. a silently dead reminder.
 */
export const hasNoRecipients = (draft: ReviewReminderSettingsDraft): boolean =>
  draft.notifyRoles.length === 0 && (!draft.sendToChannel || !draft.channel.trim());

interface PerformanceReviewReminderCardProps {
  /** Review reminder configuration draft (org-wide settings). */
  draft: ReviewReminderSettingsDraft;
  /** Editable notification message body. */
  body: string;
  /**
   * The ANNIVERSARY_REVIEW_REMINDER template's `enabled` flag acts as a master
   * switch. When off, the reminder is not sent.
   */
  enabled: boolean;
  allowedParams: string[];
  /** Validation error for the message body. */
  bodyError?: string | null;
  isResetting?: boolean;
  onEnabledChange: (value: boolean) => void;
  onChange: (patch: Partial<ReviewReminderSettingsDraft>) => void;
  onBodyChange: (value: string) => void;
  onReset: () => void;
}

/**
 * Single collapsible "Performance Review Reminder" card unifying the
 * ANNIVERSARY_REVIEW_REMINDER template body/enabled controls with the
 * organization-wide reminder settings (channel, hour, lead-time days and
 * recipient toggles). Presentational — all state is owned by the containing
 * page.
 */
export const PerformanceReviewReminderCard: React.FC<PerformanceReviewReminderCardProps> = ({
  draft,
  body,
  enabled,
  allowedParams,
  bodyError,
  isResetting,
  onEnabledChange,
  onChange,
  onBodyChange,
  onReset,
}) => {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);

  const daysBeforeAsStrings = [...draft.daysBefore]
    .sort((a, b) => b - a)
    .map((day) => day.toString());

  const handleDaysBeforeChange = (values: string[]) => {
    const days = Array.from(
      new Set(
        values
          .map((value) => parseInt(value, 10))
          .filter((day) => Number.isInteger(day) && day > 0 && day <= MAX_LEAD_TIME_DAYS)
      )
    ).sort((a, b) => b - a);
    onChange({ daysBefore: days });
  };

  const tooManyLeadTimes = hasTooManyLeadTimes(draft);
  const noRecipients = hasNoRecipients(draft);

  return (
    <Card data-testid="performance-review-reminder-card">
      <NotificationCardHeader
        title={t('reviewReminderSettings.title')}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((prev) => !prev)}
        enabled={enabled}
        onEnabledChange={onEnabledChange}
        testIdPrefix="performance-review-reminder"
      />

      {expanded && (
        <>
          <CardContent className="space-y-6">
            <p className="text-sm text-muted-foreground">
              {t('reviewReminderSettings.description')}
            </p>

            <div className="space-y-2">
              <Label htmlFor="review-reminder-channel">
                {t('reviewReminderSettings.channelLabel')}
              </Label>
              <div className="flex items-center gap-2">
                <CheckToggle
                  checked={draft.sendToChannel}
                  onCheckedChange={(sendToChannel) => onChange({ sendToChannel })}
                  label={t('reviewReminderSettings.sendToChannelLabel')}
                  testId="review-reminder-send-to-channel"
                />
                <Input
                  id="review-reminder-channel"
                  value={draft.channel}
                  onChange={(e) => onChange({ channel: e.target.value })}
                  placeholder={t('reviewReminderSettings.channelPlaceholder')}
                  disabled={!draft.sendToChannel}
                  data-testid="review-reminder-channel"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('reviewReminderSettings.channelHint')}
              </p>
            </div>

            <NotificationHourSelect
              value={draft.notificationHour}
              onChange={(notificationHour) => onChange({ notificationHour })}
              id="review-reminder-notification-hour"
              testId="review-reminder-notification-hour"
              label={t('reviewReminderSettings.notificationHourLabel')}
              hint={t('reviewReminderSettings.notificationHourHint')}
            />

            <div className="space-y-2">
              <Label>{t('reviewReminderSettings.daysBeforeLabel')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('reviewReminderSettings.daysBeforeHint')}
              </p>
              <MultiSelect
                value={daysBeforeAsStrings}
                onChange={handleDaysBeforeChange}
                placeholder={t('reviewReminderSettings.daysBeforePlaceholder')}
              />
              {tooManyLeadTimes ? (
                <NotificationCardError
                  message={t('reviewReminderSettings.errors.tooManyLeadTimes', {
                    max: MAX_LEAD_TIME_ENTRIES,
                  })}
                  testId="review-reminder-days-before-error"
                />
              ) : (
                draft.daysBefore.length === MAX_LEAD_TIME_ENTRIES && (
                  <p
                    className="text-xs text-amber-700"
                    data-testid="review-reminder-days-before-at-max"
                  >
                    {t('reviewReminderSettings.daysBeforeAtMax', { max: MAX_LEAD_TIME_ENTRIES })}
                  </p>
                )
              )}
            </div>

            <div className="space-y-3">
              <div>
                <Label>{t('reviewReminderSettings.notifyRolesLabel')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('reviewReminderSettings.notifyRolesHint')}
                </p>
              </div>
              <NotifyRoleToggles
                options={NOTIFY_ROLE_OPTIONS}
                selected={draft.notifyRoles}
                onChange={(notifyRoles) => onChange({ notifyRoles })}
                translationPrefix="reviewReminderSettings.notifyRoles"
                testIdPrefix="review-reminder"
              />
              {noRecipients && (
                <NotificationCardError
                  message={t('reviewReminderSettings.errors.noRecipients')}
                  testId="review-reminder-no-recipients-error"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="review-reminder-send-mode">
                {t('reviewReminderSettings.sendModeLabel')}
              </Label>
              <Select
                value={draft.sendMode}
                onValueChange={(value) =>
                  onChange({ sendMode: value as ReviewReminderSendMode })
                }
              >
                <SelectTrigger
                  id="review-reminder-send-mode"
                  className="w-56"
                  data-testid="review-reminder-send-mode"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(ReviewReminderSendMode).map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {t(`reviewReminderSettings.sendModes.${mode}.label`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t(`reviewReminderSettings.sendModes.${draft.sendMode}.hint`)}
              </p>
            </div>

            <NotificationMessageEditor
              id="review-reminder-body"
              testIdPrefix="review-reminder"
              body={body}
              onBodyChange={onBodyChange}
              allowedParams={allowedParams}
              error={bodyError}
            />
          </CardContent>
          <NotificationResetFooter
            onReset={onReset}
            isResetting={isResetting}
            testId="performance-review-reminder-reset"
          />
        </>
      )}
    </Card>
  );
};
